'use client';

import type { MutableRefObject, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DocumentNode } from 'graphql';
import { debounce, noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { ApolloQueryResult } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';

export type AssetHydrationQuery<T> = {
  query: DocumentNode;
  variables?: T;
  debounce: number;
};

export type SetAssetsHydrated = (
  result: ApolloQueryResult<any>,
  query: AssetHydrationQuery<any>,
  cache: HydrationCache
) => void;

export type GetAssetHydrated = (
  assetId: string,
  query: AssetHydrationQuery<any>,
  cache: HydrationCache
) => boolean;

type AssetHydrationContextValue = {
  resetHydration: (cacheKey?: string) => void;
  hydrateAssetsInWindow: (options: {
    assetIds: string[];
    cheap?: boolean;
  }) => void;
  evictIdsFromHydrationCache: (assetIds: string[]) => void;
};

const AssetHydrationContext = createContext<AssetHydrationContextValue | null>({
  resetHydration: noop,
  hydrateAssetsInWindow: noop,
  evictIdsFromHydrationCache: noop,
});

type HydrationCache = Map<string, Set<string>>;

export type AssetHydrationContextProviderProps = {
  children: ReactNode;
  queries: AssetHydrationQuery<any>[];
  setAssetsHydrated: SetAssetsHydrated;
  getAssetHydrated: GetAssetHydrated;
};

export const AssetHydrationContextProvider = memo(
  ({
    children,
    queries,
    setAssetsHydrated,
    getAssetHydrated,
  }: AssetHydrationContextProviderProps) => {
    const [firstCall, setFirstCall] = useState(true);

    const resetHydration = useCallback(() => {
      hydrationCache.clear();
    }, []);

    const client = useApolloClient();
    const mostRecentWindow = useRef<string[]>(undefined);

    const debouncedQueries = useMemo(() => {
      return queries.map((query) => {
        return debounce(
          async (assetIds: string[]) => {
            try {
              const ids = assetIds.filter((assetId) => {
                if (!assetId) {
                  return false;
                }
                return !getAssetHydrated(assetId, query, hydrationCache);
              });
              if (!ids.length) return;

              const result = await client.query({
                query: query.query,
                variables: {
                  ...(query.variables ?? {}),
                  ids,
                  page: { first: ids.length },
                },
                fetchPolicy: 'network-only',
                errorPolicy: 'ignore',
              });

              setAssetsHydrated(result, query, hydrationCache);
            } catch (err: any) {
              //the query failed here
              // eslint-disable-next-line no-console
              console.error('Unexpected asset hydration error', err);
              datadogLogger.log('Unexpected asset hydration error', err);
            }
          },
          query.debounce,
          { leading: firstCall }
        );
      });
    }, [client, firstCall, getAssetHydrated, queries, setAssetsHydrated]);

    const hydrateAssetsInWindow = useCallback(
      ({ assetIds }: { assetIds: string[] }) => {
        if (!assetIds.length) return;
        mostRecentWindow.current = assetIds;
        debouncedQueries.forEach((debouncedQuery) => debouncedQuery(assetIds));
        setFirstCall(false);
      },
      [debouncedQueries]
    );

    const evictIdsFromHydrationCache = useCallback(
      (assetIds: string[]) => {
        assetIds.forEach((assetId) => hydrationCache.delete(assetId));
        hydrateAssetsInWindow({ assetIds: mostRecentWindow.current ?? [] });
      },
      [hydrateAssetsInWindow]
    );

    const value = useMemo(
      () => ({
        resetHydration,
        hydrateAssetsInWindow,
        evictIdsFromHydrationCache,
      }),
      [hydrateAssetsInWindow, resetHydration, evictIdsFromHydrationCache]
    );

    /** when the hydration function changes, hydrate the most recent window */
    useEffect(() => {
      if (mostRecentWindow.current?.length)
        hydrateAssetsInWindow({ assetIds: mostRecentWindow.current });
    }, [hydrateAssetsInWindow]);

    /**
     * when the project changes, reset hydration since we could miss asset
     * events on the previously viewed project
     * */
    const projectId = useActiveProjectId();
    useEffect(() => {
      if (projectId !== prevProjectIdRef.current) {
        resetHydration();
        prevProjectIdRef.current = projectId;
      }
    }, [projectId, resetHydration]);

    return (
      <AssetHydrationContext.Provider value={value}>
        {children}
      </AssetHydrationContext.Provider>
    );
  }
);

export const useHydrateAssetsInWindow =
  (): AssetHydrationContextValue['hydrateAssetsInWindow'] =>
    useContextSelector(
      AssetHydrationContext,
      (ctx) => ctx?.hydrateAssetsInWindow ?? noop
    );

export const useResetAssetHydration = () =>
  useContextSelector(
    AssetHydrationContext,
    (ctx) => ctx?.resetHydration ?? noop
  );

export const useEvictFromHydrationCache = () =>
  useContextSelector(
    AssetHydrationContext,
    (ctx) => ctx?.evictIdsFromHydrationCache
  );

const prevProjectIdRef: MutableRefObject<string | undefined> = {
  current: undefined,
};
const hydrationCache: HydrationCache = new Map();
